/**
 * DeltaIndicator Component
 *
 * Displays trend direction and delta value with neutral/grey coloring.
 * Used consistently across all reports and dashboards.
 *
 * Atomic level: Atom
 */

import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '../../../../lib/utils';

export interface DeltaIndicatorProps {
  /** The delta value to display */
  delta: number;
  /** Whether to show the numeric value (default: true) */
  showValue?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Threshold below which delta is considered neutral (default: 0) */
  neutralThreshold?: number;
  /** Format as percentage (adds % suffix) */
  isPercentage?: boolean;
  /** Which direction is positive — 'up' (default, green when rising) or 'down' (green when falling, e.g. conduct score) */
  trendPositive?: 'up' | 'down';
  /** Additional class names */
  className?: string;
}

/**
 * Delta indicator showing change direction and value.
 *
 * Always uses neutral/grey coloring for consistency.
 *
 * @example
 * ```tsx
 * <DeltaIndicator delta={0.4} />           // Shows: ↑ +0.4
 * <DeltaIndicator delta={-2.5} />          // Shows: ↓ -2.5
 * <DeltaIndicator delta={0} />             // Shows: —
 * <DeltaIndicator delta={5.2} isPercentage /> // Shows: ↑ +5.2%
 * ```
 */
export function DeltaIndicator({
  delta,
  showValue = true,
  size = 'md',
  neutralThreshold = 0,
  isPercentage = false,
  trendPositive,
  className,
}: DeltaIndicatorProps) {
  // Determine icon based on delta value
  const getIcon = () => {
    if (Math.abs(delta) <= neutralThreshold) {
      return Minus;
    }
    return delta > 0 ? TrendingUp : TrendingDown;
  };

  const Icon = getIcon();

  // Size configurations
  const sizeConfig = {
    sm: { icon: 12, text: 'text-xs', gap: 'gap-0.5' },
    md: { icon: 16, text: 'text-sm', gap: 'gap-1' },
    lg: { icon: 20, text: 'text-base', gap: 'gap-1.5' },
  };

  const config = sizeConfig[size];

  // Format delta value
  const formatDelta = () => {
    if (!showValue) return null;

    const absValue = Math.abs(delta);
    const prefix = delta > 0 ? '+' : delta < 0 ? '-' : '';
    const suffix = isPercentage ? '%' : '';

    // Format based on value type
    if (Number.isInteger(absValue)) {
      return `${prefix}${absValue}${suffix}`;
    }
    return `${prefix}${absValue.toFixed(1)}${suffix}`;
  };

  // Determine color: neutral (grey) by default, or red/green when trendPositive is set
  const getColorClass = () => {
    if (!trendPositive || Math.abs(delta) <= neutralThreshold) {
      return 'text-muted-foreground';
    }
    const isPositiveDirection = delta > 0;
    const isGood = trendPositive === 'up' ? isPositiveDirection : !isPositiveDirection;
    return isGood ? 'text-green-600' : 'text-red-500';
  };

  return (
    <div
      className={cn(
        'flex items-center',
        getColorClass(),
        config.gap,
        className
      )}
    >
      <Icon size={config.icon} />
      {showValue && (
        <span className={cn('font-medium tabular-nums', config.text)}>
          {formatDelta()}
        </span>
      )}
    </div>
  );
}
